import { FormEvent, useState } from "react";
import {
  inventoryApi,
  type InventoryLocation,
} from "../lib/inventory";
import { useRpcResource } from "../lib/useRpcResource";
import { ErrorPanel, Loading } from "./ResourceView";

const today = () => new Date().toISOString().slice(0, 10);

export function CreateSerializedPartCard({
  organizationId,
  locations,
  onClose,
  onCreated,
}: {
  organizationId: string;
  locations: InventoryLocation[];
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}) {
  const setup = useRpcResource(
    () => inventoryApi.serializedPartSetup(organizationId),
    [organizationId],
  );
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [unit, setUnit] = useState("each");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState("");
  const [identifierType, setIdentifierType] = useState("serial_number");
  const [identifierValue, setIdentifierValue] = useState("");
  const [acquisitionDate, setAcquisitionDate] = useState(today);
  const [acquisitionCost, setAcquisitionCost] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryReady =
    Boolean(category) && (category !== "__new__" || Boolean(newCategory.trim()));
  const canSubmit =
    Boolean(sku.trim()) &&
    Boolean(name.trim()) &&
    categoryReady &&
    Boolean(locationId) &&
    Boolean(identifierValue.trim()) &&
    Boolean(acquisitionDate) &&
    acquisitionCost !== "" &&
    Number(acquisitionCost) >= 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const result = await inventoryApi.createSerializedPart({
      organizationId,
      sku: sku.trim(),
      name: name.trim(),
      homeLocationId: locationId,
      identifierType,
      identifierValue: identifierValue.trim(),
      categoryId: category === "__new__" ? null : category,
      newCategoryName:
        category === "__new__" ? newCategory.trim() : null,
      description: description.trim() || null,
      unitOfMeasure: unit.trim() || "each",
      acquisitionDate,
      acquisitionCost: Number(acquisitionCost),
      notes: notes.trim() || null,
    });
    setBusy(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    await onCreated();
  }

  return (
    <section className="card inventory-new-part-card">
      <div className="inventory-new-part-heading">
        <div>
          <h2>Create new serialized part</h2>
          <p className="muted">
            Creates the part, its category, and its first individually tracked
            asset in one step.
          </p>
        </div>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
      {setup.loading && !setup.data ? (
        <Loading />
      ) : setup.error ? (
        <ErrorPanel message={setup.error} />
      ) : (
        <form className="inventory-new-part-form" onSubmit={submit}>
          {error && <ErrorPanel message={error} />}
          <div className="form-grid four">
            <label className="field">
              <span>Part SKU</span>
              <input
                value={sku}
                onChange={(event) => setSku(event.target.value)}
                placeholder="Example: LEG-15M-001"
                required
              />
            </label>
            <label className="field">
              <span>Part name</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Example: 15m upright leg"
                required
              />
            </label>
            <label className="field">
              <span>Category</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                required
              >
                <option value="">Select category</option>
                <option value="__new__">+ Create new category</option>
                {(setup.data?.categories ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            {category === "__new__" ? (
              <label className="field">
                <span>New category name</span>
                <input
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  placeholder="Example: Structure legs"
                  required
                />
              </label>
            ) : (
              <label className="field">
                <span>Unit of measure</span>
                <input
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                />
              </label>
            )}
            <label className="field">
              <span>Primary identifier</span>
              <select
                value={identifierType}
                onChange={(event) => setIdentifierType(event.target.value)}
              >
                <option value="serial_number">Serial Number</option>
                <option value="qr_code">QR Code</option>
                <option value="barcode">Barcode</option>
                <option value="rfid_epc">RFID EPC</option>
              </select>
            </label>
            <label className="field">
              <span>Identifier value</span>
              <input
                value={identifierValue}
                onChange={(event) => setIdentifierValue(event.target.value)}
                placeholder="Scan or enter the identifier"
                required
              />
            </label>
            <label className="field">
              <span>Home location</span>
              <select
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
                required
              >
                <option value="">Select location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Acquisition date</span>
              <input
                type="date"
                value={acquisitionDate}
                onChange={(event) => setAcquisitionDate(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Acquisition cost</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={acquisitionCost}
                onChange={(event) => setAcquisitionCost(event.target.value)}
                placeholder="0.00"
                required
              />
            </label>
            {category === "__new__" && (
              <label className="field">
                <span>Unit of measure</span>
                <input
                  value={unit}
                  onChange={(event) => setUnit(event.target.value)}
                />
              </label>
            )}
            <label className="field span-two">
              <span>Part description</span>
              <input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Size, material, manufacturer, or intended use"
              />
            </label>
            <label className="field span-two">
              <span>Asset notes</span>
              <input
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Purchase source, condition, or receiving notes"
              />
            </label>
          </div>
          <div className="inventory-new-part-footer">
            <span>
              Tracking is automatically set to <strong>Serialized</strong> and
              inventory tracked.
            </span>
            <button className="primary" disabled={busy || !canSubmit}>
              {busy ? "Creating…" : "Create part & serialized asset"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
